import { useCallback, useState } from 'react';
import { FeatureFlagEnum } from '../../../featureflag/feature-flag.enum';
import { UseDataQueryKey, UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useAuthContext } from '../../../modules/login/AuthContext';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import useSystemParametersStore from '../../../stores/SystemParameters/useSystemParameters.store';
import {
  getProductColorSelloutKey,
  getTotalProductColorSelloutKey,
} from './product-color-sellout.keys';
import { getProductColorSelloutRepository } from './product-color-sellout.repository';
import {
  GetProductColorSelloutMetricsParams,
  GetProductColorsSelloutTotalsParams,
  SelloutProductColor,
  SelloutTotalsProductColor,
} from './product-color-sellout.types';
import { availableToShowSellout } from './productColorSellout.utils';

const useCanGetSelloutData = () => {
  const { getFeatureFlag } = useFeatureFlag();
  const { loggedin } = useAuthContext();

  return getFeatureFlag(FeatureFlagEnum.ENABLE_SELLOUT_IN_COMMERCE) && loggedin;
};

export const useGetProductColorSelloutMetrics = (params: GetProductColorSelloutMetricsParams) => {
  return useDataQuery<SelloutProductColor | null>(
    {
      queryKey: getProductColorSelloutKey(params),
      queryFn: async () => {
        const response =
          await getProductColorSelloutRepository().getProductColorSelloutMetrics(params);
        if (!response.ok || !response.data) {
          return null;
        }
        return response.data;
      },
      refetchOnMount: false,
    },
    { enabled: useCanGetSelloutData() },
  );
};

export const useGetProductColorsSelloutTotals = (
  params: GetProductColorsSelloutTotalsParams,
  options?: UseDataQueryOptions<SelloutTotalsProductColor[]>,
) => {
  const queryKey: UseDataQueryKey = getTotalProductColorSelloutKey(params);

  return useDataQuery<SelloutTotalsProductColor[]>(
    {
      queryKey,
      queryFn: async () => {
        const response =
          await getProductColorSelloutRepository().getTotalProductColorsSellout(params);
        if (!response.ok || !response.data) {
          return [];
        }
        return response.data;
      },
      refetchOnMount: false,
    },
    { ...options, enabled: useCanGetSelloutData() && (options?.enabled ?? true) },
  );
};

export function useGetProductColorsSelloutPage() {
  const canGetSelloutData = useCanGetSelloutData();

  const selectedOrder = useOrderBookStore((state) => state.selectedOrder);

  const [selloutData, setSelloutData] = useState<SelloutTotalsProductColor[]>([]);

  const { getBrandSystemParameterValue } = useSystemParametersStore();

  const fetchData = useCallback(
    async (productColorIds: string[]) => {
      if (!canGetSelloutData) {
        return;
      }

      const response = await getProductColorSelloutRepository().getTotalProductColorsSellout({
        productColorIds,
        orderId: selectedOrder?.id,
      });

      if (!response.data || !response.ok || response.data?.length <= 0) {
        return;
      }

      const newData = (response.data || []).filter(({ totalQuantitySold }) =>
        availableToShowSellout(
          totalQuantitySold,
          getBrandSystemParameterValue(FeatureFlagEnum.COMMERCE_MIN_VALUE_TO_SHOW_SELLOUT_TAG),
        ),
      );

      setSelloutData((currentData) => [...newData, ...(currentData || [])]);
    },
    [getBrandSystemParameterValue, selectedOrder?.id, canGetSelloutData],
  );

  return { fetchData, selloutData };
}
